/**
 * Decode the `?d=<base64url>` autologin blob from a Customer.io email link
 * (architecture §5.1). The blob is base64url-encoded JSON of `{ email, studyId }`
 * — `studyId` is hardcoded into each campaign's email template; `email` varies
 * per recipient.
 *
 * Not strong opacity — a determined user can decode + edit + re-encode the
 * blob to take an interview as someone else. Damage is bounded by the
 * `UNIQUE(user_id, study_id)` on `interviews` (architecture §5.1). HMAC was
 * deliberately rejected: there's no sensitive PII in the URL, and the cost of
 * key management isn't worth a marginal abuse-prevention gain.
 */

export interface AutologinPayload {
  email: string;
  studyId: string;
}

/** Decode a base64url JSON blob. Returns null when the input is missing,
 *  malformed, or doesn't carry the expected fields. */
export function decodeAutologin(d: string | null): AutologinPayload | null {
  if (!d) return null;
  try {
    // base64url → standard base64 (add padding back)
    const standard = d.replace(/-/g, "+").replace(/_/g, "/");
    const padded =
      standard + "=".repeat((4 - (standard.length % 4)) % 4);
    const json = atob(padded);
    const parsed = JSON.parse(json) as Partial<AutologinPayload>;
    if (typeof parsed.email !== "string" || typeof parsed.studyId !== "string") {
      return null;
    }
    return { email: parsed.email, studyId: parsed.studyId };
  } catch {
    return null;
  }
}
